// TASK-3 RESTYLE: classNames restored to LAB's light-theme classes
// (border-border/bg-background/text-signal for errors). Behavior, copy and
// the `endpoint`/`tz` contract are unchanged.
import { useState } from "react";
import { SlotPicker } from "./SlotPicker";
import { Button } from "./Button";
import { validateDemoRequest, type PreferredSlot } from "@/lib/demoRequest";

const fieldCls =
  "w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground transition-shadow focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40";

type Status = "idle" | "submitting" | "success" | "error";

export function DemoForm({ endpoint, tz }: { endpoint: string; tz: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [useCase, setUseCase] = useState("");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState(""); // honeypot
  const [slots, setSlots] = useState<PreferredSlot[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");

  if (status === "success") {
    return (
      <div className="rounded border border-border bg-card p-8">
        <h3 className="text-lg font-semibold">Thanks — we'll be in touch.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll email you to confirm one of your preferred times and set up the demo.
        </p>
      </div>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name,
      email,
      company,
      teamSize,
      useCase,
      message,
      preferredSlots: slots,
      submittedAt: new Date().toISOString(),
    };
    const result = validateDemoRequest(payload);
    if (!result.ok) {
      const errs = { ...result.errors };
      if (slots.length === 0) errs.preferredSlots = "Please add at least one preferred slot.";
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, _hp: hp }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <Field id="name" label="Your name" value={name} onChange={setName} error={errors.name} />
      <Field
        id="email"
        label="Work email"
        type="email"
        value={email}
        onChange={setEmail}
        error={errors.email}
      />
      <Field
        id="company"
        label="Company"
        value={company}
        onChange={setCompany}
        error={errors.company}
      />
      <Field
        id="teamSize"
        label="Team size"
        value={teamSize}
        onChange={setTeamSize}
        error={errors.teamSize}
        placeholder="e.g. 2-10"
      />
      <div className="flex flex-col gap-1 text-sm">
        <label htmlFor="useCase">Primary use case</label>
        <textarea
          id="useCase"
          className={fieldCls}
          rows={3}
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
        />
        {errors.useCase !== undefined && (
          <span className="text-xs text-signal">{errors.useCase}</span>
        )}
      </div>
      <div className="flex flex-col gap-1 text-sm">
        <label htmlFor="message">Anything else? (optional)</label>
        <textarea
          id="message"
          className={fieldCls}
          rows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm">Preferred times</span>
        <SlotPicker value={slots} onChange={setSlots} tz={tz} />
        {errors.preferredSlots !== undefined && (
          <span className="text-xs text-signal">{errors.preferredSlots}</span>
        )}
      </div>

      {/* Honeypot — hidden from humans, off the a11y tree. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
      />

      {status === "error" && (
        <p className="text-sm text-signal">
          Something went wrong sending your request. Please try again or email us directly.
        </p>
      )}
      <Button type="submit" className="self-start">
        {status === "submitting" ? "Sending…" : "Request demo"}
      </Button>
    </form>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  type = "text",
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  type?: string | undefined;
  placeholder?: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        className={fieldCls}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(placeholder !== undefined ? { placeholder } : {})}
      />
      {error !== undefined && <span className="text-xs text-signal">{error}</span>}
    </div>
  );
}
